import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery as useConvexQuery, useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import { useState } from 'react'
import { ArrowLeft, Play, Pause, BookOpen, Trash2, Plus } from 'lucide-react'
import { DeckColorPicker } from '@/components/DeckColorPicker'
import { DeckFrequencyPicker } from '@/components/DeckFrequencyPicker'
import { DeckActivationModal } from '@/components/DeckActivationModal'
import { RemoveDeckModal } from '@/components/RemoveDeckModal'
import { ErrorBoundary } from '@/components/ErrorBoundary'

export const Route = createFileRoute('/app/decks/$deckId')({
  component: DeckDetailPage,
})

function DeckDetailPage() {
  const { deckId } = Route.useParams()
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to view deck details</div>
      </div>
    )
  }

  return <DeckDetailContent deckId={deckId as Id<'flashcardDecks'>} userId={currentUser._id} />
}

function DeckDetailContent({
  deckId,
  userId,
}: {
  deckId: Id<'flashcardDecks'>
  userId: Id<'users'>
}) {
  const navigate = useNavigate()
  const [showActivationModal, setShowActivationModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [isAddingDeck, setIsAddingDeck] = useState(false)

  // Fetch deck data
  const decks = useConvexQuery(api.flashcards.getAllDecksWithCounts, { userId })
  const deck = decks?.find((d) => d._id === deckId)

  // Mutation to copy system deck to user collection
  const copySystemDeck = useMutation(api.flashcards.copySystemDeckToUser)

  // Mutation to delete flashcard
  const deleteFlashcardMutation = useMutation(api.flashcards.deleteFlashcard)

  // Fetch deck statistics
  const statistics = useConvexQuery(api.flashcards.getDeckStatistics, { deckId })

  // Fetch flashcards in this deck
  const flashcards = useConvexQuery(api.flashcards.getDeckFlashcards, { deckId })

  if (decks === undefined || statistics === undefined || flashcards === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading deck details...</div>
      </div>
    )
  }

  if (!deck) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Deck not found</div>
      </div>
    )
  }

  // Derive status directly from server state (single source of truth)
  const isActive = (deck.status ?? 'active') === 'active'
  const defaultColor = deck.isSystem ? '#0ea5e9' : '#10b981'

  // Handle adding system deck to user's collection
  const handleAddSystemDeck = async () => {
    if (!deck.isSystem) return

    setIsAddingDeck(true)
    try {
      const result = await copySystemDeck({
        userId,
        systemDeckId: deckId,
      })

      if (result.created) {
        // Navigate to the newly created user deck
        navigate({ to: `/app/decks/${result.deckId}` })
      } else {
        // Deck already exists in user's collection, navigate to it
        navigate({ to: `/app/decks/${result.deckId}` })
      }
    } catch (error) {
      console.error('Error adding system deck:', error)
      alert(error instanceof Error ? error.message : 'Failed to add deck to your collection')
    } finally {
      setIsAddingDeck(false)
    }
  }

  // Handle deleting a flashcard
  const handleDeleteFlashcard = async (flashcardId: Id<'flashcards'>) => {
    if (!confirm('Are you sure you want to delete this flashcard? This cannot be undone.')) {
      return
    }

    try {
      await deleteFlashcardMutation({
        flashcardId,
        userId,
      })
    } catch (error) {
      console.error('Error deleting flashcard:', error)
      alert(error instanceof Error ? error.message : 'Failed to delete flashcard')
    }
  }

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-6">
          <Link
            to="/app/decks"
            className="inline-flex items-center gap-2 text-sky-blue hover:text-terminal-green transition-colors font-mono mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>BACK TO DECKS</span>
          </Link>

          <div className="terminal-border p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue font-mono tracking-wider">
                    {deck.name}
                  </h1>
                  <span
                    className={`text-xs font-mono px-2 py-1 border-2 ${
                      isActive
                        ? 'bg-terminal-green/10 border-terminal-green text-terminal-green'
                        : 'bg-trail-tan/10 border-trail-tan text-trail-tan'
                    }`}
                  >
                    {isActive ? 'ACTIVE' : 'PAUSED'}
                  </span>
                  {deck.isSystem && (
                    <span className="text-xs font-mono px-2 py-1 border-2 bg-sky-blue/10 border-sky-blue text-sky-blue">
                      SYSTEM
                    </span>
                  )}
                </div>
                {deck.description && (
                  <p className="text-trail-tan font-mono text-sm sm:text-base">
                    {deck.description}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                {/* System Deck: Show "Add to My Decks" button */}
                {deck.isSystem ? (
                  <button
                    onClick={handleAddSystemDeck}
                    disabled={isAddingDeck}
                    className={`flex items-center gap-2 px-4 py-2 sm:py-2.5 text-sm sm:text-lg font-bold border-2 border-terminal-green text-terminal-green hover:bg-terminal-green/10 font-mono transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isAddingDeck ? (
                      <>
                        <div className="w-4 h-4 border-2 border-terminal-green border-t-transparent rounded-full animate-spin" />
                        <span>ADDING...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">ADD TO MY DECKS</span>
                        <span className="sm:hidden">ADD</span>
                      </>
                    )}
                  </button>
                ) : (
                  <>
                    {/* User Deck Controls */}
                    {/* Color Picker */}
                    <ErrorBoundary>
                      <DeckColorPicker
                        deckId={deckId}
                        currentColor={deck.color}
                        defaultColor={defaultColor}
                      />
                    </ErrorBoundary>

                    {/* Frequency Picker */}
                    <ErrorBoundary>
                      <DeckFrequencyPicker
                        deckId={deckId}
                        currentFrequency={deck.reviewFrequency}
                      />
                    </ErrorBoundary>

                    {/* Activation Toggle Button */}
                    <button
                      onClick={() => setShowActivationModal(true)}
                      className={`flex items-center gap-2 px-4 py-2 sm:py-2.5 text-sm sm:text-lg font-bold border-2 font-mono transition-all ${
                        isActive
                          ? 'border-trail-tan text-trail-tan hover:bg-trail-tan/10'
                          : 'border-terminal-green text-terminal-green hover:bg-terminal-green/10'
                      }`}
                    >
                      {isActive ? (
                        <>
                          <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span>PAUSE</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span>ACTIVATE</span>
                        </>
                      )}
                    </button>

                    {/* Remove Deck Button (only for user decks) */}
                    {!deck.isDefault && (
                      <button
                        onClick={() => setShowRemoveModal(true)}
                        className="flex items-center gap-2 px-4 py-2 sm:py-2.5 text-sm sm:text-lg font-bold border-2 border-red-500 text-red-500 hover:bg-red-500/10 font-mono transition-all"
                      >
                        <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">REMOVE</span>
                        <span className="sm:hidden">DEL</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Panel */}
        {statistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="terminal-border p-4 bg-black">
              <div className="text-trail-tan font-mono text-xs mb-1">TOTAL CARDS</div>
              <div className="text-sky-blue font-mono text-2xl font-bold">
                {statistics.totalCards}
              </div>
            </div>

            <div className="terminal-border p-4 bg-black">
              <div className="text-trail-tan font-mono text-xs mb-1">LEARNED</div>
              <div className="text-terminal-green font-mono text-2xl font-bold">
                {statistics.learnedCards}
              </div>
            </div>

            <div className="terminal-border p-4 bg-black">
              <div className="text-trail-tan font-mono text-xs mb-1">COMPLETION</div>
              <div className="text-sky-blue font-mono text-2xl font-bold">
                {statistics.completionPercentage}%
              </div>
            </div>

            <div className="terminal-border p-4 bg-black">
              <div className="text-trail-tan font-mono text-xs mb-1">LAST REVIEWED</div>
              <div className="text-trail-tan font-mono text-sm">
                {statistics.lastReviewedAgo || 'Never'}
              </div>
            </div>
          </div>
        )}

        {/* Flashcards List */}
        <div className="terminal-border p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-terminal-green" />
            <h2 className="text-xl font-bold text-terminal-green font-mono tracking-wider">
              [FLASHCARDS]
            </h2>
          </div>

          {flashcards.length === 0 ? (
            <div className="text-center py-8 text-trail-tan font-mono">
              No flashcards in this deck yet
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {flashcards.map((card) => (
                <div
                  key={card._id}
                  className="p-3 border-2 border-trail-tan hover:border-sky-blue transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-white font-mono text-sm mb-1">
                        {card.frontSpanish.join(' ')}
                      </div>
                      <div className="text-trail-tan font-mono text-xs">
                        {card.backEnglish.join(' ')}
                      </div>
                    </div>
                    <div className="flex gap-2 items-center">
                      {card.isLearned && (
                        <span className="text-xs font-mono px-2 py-1 border-2 bg-terminal-green/10 border-terminal-green text-terminal-green">
                          LEARNED
                        </span>
                      )}
                      {card.isArchived && (
                        <span className="text-xs font-mono px-2 py-1 border-2 bg-trail-tan/10 border-trail-tan text-trail-tan">
                          ARCHIVED
                        </span>
                      )}
                      {!deck.isSystem && (
                        <button
                          onClick={() => handleDeleteFlashcard(card._id)}
                          className="p-1.5 border-2 border-red-500 text-red-500 hover:bg-red-500/10 transition-all"
                          title="Delete flashcard"
                          aria-label="Delete flashcard"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activation Modal */}
      {showActivationModal && (
        <ErrorBoundary>
          <DeckActivationModal
            deckId={deckId}
            deckName={deck.name}
            currentStatus={deck.status ?? 'active'}
            onClose={() => setShowActivationModal(false)}
            onStatusChanged={() => {
              // Convex real-time subscription will update deck.status automatically
              setShowActivationModal(false)
            }}
          />
        </ErrorBoundary>
      )}

      {/* Remove Deck Modal */}
      {showRemoveModal && (
        <ErrorBoundary>
          <RemoveDeckModal
            deckId={deckId}
            deckName={deck.name}
            flashcardCount={flashcards.length}
            onClose={() => setShowRemoveModal(false)}
          />
        </ErrorBoundary>
      )}
    </div>
  )
}
